/**
 * `go` narrowing (D31, D227, D231) — a bearing, a named thing, or both.
 *
 * @remarks
 * Extracted from `narrow-parsed-command.ts` at the 500-line cap. The rule
 * that grew it is D231's: when a bearing and a thing arrive together, the
 * thing decides — see {@link narrowGoCommand}.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { speakSlot } from '../defaults/default-responses.js';
import { itemIdForEntityRef, roomIdForEntityRef } from '../entities/entity-ref.js';
import { narrowed, rejected } from './narrow-parsed-command.js';

/**
 * Narrow `go`.
 *
 * @remarks
 * A bearing alone moves by the bearing. A named thing alone (D31: `go to the
 * stairwell`) narrows onto the thing, and `handleGo` resolves it through the
 * room's exit links. D231 (Calder's P7 §1): a bearing WITH a named thing —
 * `enter the door`, `go in the stairwell`, `descend the stairs` — is decided
 * by the THING, not the bearing: `enter` is an alias of `in`, and reading it
 * as the bearing walked the gatehouse's visitor through a sealed blast door
 * to the stairhead. A thing linked to an exit here is that exit, whatever
 * the bearing; a thing linked to none is answered as a destination no way
 * here leads to (`noPathToThat`), never by a bearing the player did not
 * mean. The room's own words after a bearing (`go up the road`, D227) are
 * color, and the bearing stands — the player named where they are, not
 * where they are going. Without a state to read exits from (tooling), the
 * bearing stands as it always has.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @returns The narrowed action, or the missing-direction refusal
 */
export const narrowGoCommand = (command: ParsedCommand, bundle: EngineGameBundle, state: RuntimeState | undefined): CommandNarrowingOutcome => {
	const namedRef = command.directObjectRef ?? command.indirectObjectRef ?? command.toolRef;
	const namedThingId = namedRef === undefined || roomIdForEntityRef(namedRef) !== undefined ? undefined : itemIdForEntityRef(namedRef);

	if (command.direction !== undefined && (namedThingId === undefined || state === undefined)) {
		return narrowed({ directionToken: command.direction, type: 'go' });
	}

	if (namedThingId !== undefined) {
		return narrowed({ destinationItemId: namedThingId, type: 'go' });
	}

	return rejected(bundle, state, { type: 'missing_direction' }, [speakSlot(bundle, state, 'missingDirection')]);
};
